// src/surveyStyles.js
// Centralized styling for the Intercom Survey App

const baseFontFamily = "'SF Pro', system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'SF Pro Icons', 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif";

export const styles = {
    rootContainer: {
        fontFamily: baseFontFamily,
        paddingBottom: "120px", // Increased from 100px to prevent content being hidden
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        minHeight: "100vh",
        width: "100%",
        transition: "opacity 0.5s ease-in-out",
        boxSizing: "border-box",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#FFFFFF",  // ADD THIS LINE
        
    },
    contentWrapper: {
        padding: "32px 24px",
        maxWidth: "500px",
        margin: "0 auto",
        width: "100%",
        boxSizing: "border-box",
        height: "calc(100vh - 80px)",  // Leave 80px for button area
        overflowY: "auto",  // Allow scrolling within content area
        paddingBottom: "40px",  // Extra padding at bottom of scrollable area
    },
    title: {
        fontFamily: baseFontFamily,
        fontSize: "28px",
        fontWeight: "400",
        lineHeight: "36px",
        letterSpacing: "-0.12px",
        background: "linear-gradient(90deg, #713094 0.26%, #1E1169 40.32%, #A988C8 58.79%, #091C49 84.65%)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "transparent",
        marginBottom: "16px",
        minHeight: "36px",
    },
    previousQuestionTitle: {
        fontFamily: baseFontFamily,
        fontSize: "28px",
        fontWeight: "400",
        lineHeight: "36px",
        letterSpacing: "-0.12px",
        opacity: 0.3,
        background: "linear-gradient(90deg, #713094 0.26%, #1E1169 40.32%, #A988C8 58.79%, #091C49 84.65%)",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "transparent",
        filter: "blur(3px)",
        marginBottom: "8px",
        minHeight: "36px",
    },
    description: {
        fontFamily: baseFontFamily,
        fontSize: "18px",
        fontWeight: "400",
        lineHeight: "22px",
        letterSpacing: "-0.18px",
        color: "#475569",
        marginTop: "8px",
    },
    inputField: {
        width: "100%",
        padding: "16px",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        color: "#020617",
        fontSize: "18px",
        fontFamily: baseFontFamily,
        outline: "none",
        transition: "all 0.3s ease",
        boxSizing: "border-box",
    },
    textareaField: {
        width: "100%",
        minHeight: "120px",
        padding: "16px",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        color: "#020617",
        fontSize: "18px",
        fontFamily: baseFontFamily,
        outline: "none",
        resize: "vertical",
        transition: "all 0.3s ease",
        boxSizing: "border-box",
    },
    inputFieldFocused: {
        border: "2px solid #713094",
        boxShadow: "0 0 0 3px rgba(113, 48, 148, 0.1)",
        padding: "15px",
    },
    sectionTitle: {
        fontSize: "20px",
        fontWeight: "600",
        color: "#020617",
        fontFamily: baseFontFamily,
        marginBottom: "12px",
        textAlign: "center",
    },
    questionContainer: {
        marginBottom: "32px",
    },
    primaryButton: (disabled, isHovered) => ({
        backgroundColor: disabled 
            ? "#E2E8F0" 
            : isHovered 
                ? "#333333"  // Darker black on hover
                : "#000000", // BLACK primary button
        color: disabled ? "#94A3B8" : "white",
        border: "none",
        borderRadius: "900px",
        padding: "12px 24px",
        fontSize: "16px",
        fontWeight: "400",
        fontFamily: baseFontFamily,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.2s ease-in-out",
        textDecoration: 'none',
        position: "relative",
        width: "100%",
        height: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
    }),
    secondaryButton: (disabled, isHovered) => ({
        backgroundColor: isHovered ? "#f0f0f0" : "white",
        color: "#020617",
        border: "1px solid #020617",
        borderRadius: "900px",
        padding: "12px 24px",
        fontSize: "16px",
        fontWeight: "400",
        fontFamily: baseFontFamily,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "48px",
        transition: "background-color 0.2s ease-in-out",
    }),
    // Button container wrapper - ensures equal distribution
    buttonWrapper: {
        display: "flex",
        width: "100%",
        gap: "16px",
        alignItems: "stretch", // Ensures equal height
    },
    // Single button wrapper (full width)
    singleButtonWrapper: {
        width: "100%",
        display: "flex",
    },
    // Dual button wrapper (equal width distribution)
    dualButtonWrapper: {
        flex: "1 1 0", // This ensures equal width distribution
        minWidth: 0, // Prevents flex items from overflowing
        display: "flex",
    },
    // Sticky button container for action buttons
    stickyButtonContainer: {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        padding: "16px 24px",
        paddingBottom: "calc(16px + env(safe-area-inset-bottom, 20px))", // KEY FIX for mobile browsers
        boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "16px",
        zIndex: 100,
        minHeight: "80px", // Ensures consistent height
    },
    // Non-sticky button container (for single buttons like "Begin reflection")
    staticButtonContainer: {
        marginTop: "24px",
        width: "100%",
    },
    label: {
        border: "0",
        clip: "rect(0 0 0 0)",
        height: "1px",
        margin: "-1px",
        overflow: "hidden",
        padding: "0",
        position: "absolute",
        width: "1px",
        fontFamily: baseFontFamily,
    },
    errorText: {
        color: "#DC2626",
        fontSize: "14px",
        marginTop: "4px",
        fontFamily: baseFontFamily,
    },
    reviewQuestionText: {
        color: "#475569",
        fontFamily: baseFontFamily,
        fontSize: "16px",
        fontWeight: "400",
        lineHeight: "22px",
        letterSpacing: "-0.3px",
        marginBottom: "4px",
    },
    reviewAnswerText: {
        color: "#020617",
        fontFamily: baseFontFamily,
        fontSize: "16px",
        fontWeight: "400",
        lineHeight: "22px",
        letterSpacing: "-0.3px",
        marginBottom: "16px",
        whiteSpace: "pre-wrap",
    },
    reviewScrollContainer: {
        maxHeight: "calc(100vh - 280px)",  // Account for title, buttons, and padding
        overflowY: "auto",
        paddingRight: "8px",
        marginRight: "-8px",
    },

    
    loadingIndicatorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '150px',
        width: '100%',
    },
    submittingContainer: {
        textAlign: 'center',
        padding: '40px 0'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #e2e8f0',
        borderTopColor: '#713094',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 20px'
    },
    submittingTitle: {
        marginBottom: '12px',
        fontSize: '20px',
        fontWeight: '600',
        color: '#1a202c',
        fontFamily: baseFontFamily,
    },
    submittingDescription: {
        color: '#6b7280',
        fontSize: '16px',
        lineHeight: '1.5',
        fontFamily: baseFontFamily,
    },
    ratingContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        marginTop: '24px',
        marginBottom: '16px',
    },
    ratingLabels: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '14px',
        color: '#64748B',
        fontFamily: baseFontFamily,
        marginTop: '8px',
    },
};

// Animation configurations
export const animations = {
    titleDuration: 700,
    descriptionDuration: 900,
    elementRevealDuration: 600,
    elementRevealDelayIncrement: 200,
};